/**
 * Patient Notes Hook
 *
 * CRUD operations for patient notes (journal entries).
 *
 * Lifecycle policy (#2244): patient notes are patient-authored free text —
 * NOT verified clinical records — and the API supports the full lifecycle
 * including hard delete (contract PUT+DELETE on the item path; delete
 * round-trip verified live on dev). There is no server-side soft-delete or
 * versioning; if clinical policy ever requires an audit trail for notes,
 * that is a backend change, not a client workaround.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import { createIdempotencyKey } from "../../api/idempotency";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import { patientNoteKeys } from "../patient-cache-keys";
import { removePatientNote } from "../patient-note-lifecycle";

export { patientNoteKeys };

// ============================================================================
// Types
// ============================================================================

type NotesResponse =
  paths["/{tenantId}/patients/{patientId}/notes"]["get"]["responses"]["200"]["content"]["application/json"];
type CreateNoteBody = NonNullable<
  paths["/{tenantId}/patients/{patientId}/notes"]["post"]["requestBody"]
>["content"]["application/json"];
type UpdateNoteBody = NonNullable<
  paths["/{tenantId}/patients/{patientId}/notes/{noteId}"]["put"]["requestBody"]
>["content"]["application/json"];

export type PatientNote = NotesResponse["data"][number];
export type CreateNotePayload = CreateNoteBody;
export type CreateNoteVariables = CreateNotePayload & {
  /** Reused when the same user submission is retried after an uncertain response. */
  idempotencyKey?: string;
};
export type UpdateNotePayload = UpdateNoteBody & { noteId: string };

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch all notes for the current patient.
 */
export function usePatientNotes() {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: patientNoteKeys.list(tenantId ?? "", patientId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/notes",
        {
          params: {
            path: { tenantId, patientId },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load patient notes",
          "usePatientNotes",
          response?.status,
        );
      }
    },
    enabled: !!tenantId && !!patientId,
  });
}

/**
 * Hook to create a new patient note.
 */
export function useCreatePatientNote() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idempotencyKey, ...payload }: CreateNoteVariables) => {
      const { data, error, response } = await client.POST(
        "/{tenantId}/patients/{patientId}/notes",
        {
          params: {
            path: { tenantId, patientId },
          },
          headers: {
            "Idempotency-Key": idempotencyKey ?? createIdempotencyKey(),
          },
          body: payload,
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to create patient note",
          "useCreatePatientNote",
          response?.status,
        );
      }
    },
    onMutate: async (payload) => {
      const listKey = patientNoteKeys.list(tenantId ?? "", patientId ?? "");
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<PatientNote[]>(listKey);

      const optimistic = {
        id: `temp-note-${Date.now()}`,
        patientId: patientId ?? "",
        date: payload.date,
        text: payload.text,
        cycleId: payload.cycleId ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as PatientNote;

      queryClient.setQueryData<PatientNote[]>(listKey, (old) =>
        old ? [optimistic, ...old] : [optimistic],
      );

      return { previous, listKey };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: patientNoteKeys.patient(tenantId ?? "", patientId ?? ""),
      });
    },
  });
}

/**
 * Hook to update an existing patient note.
 */
export function useUpdatePatientNote() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, ...payload }: UpdateNotePayload) => {
      const { data, error, response } = await client.PUT(
        "/{tenantId}/patients/{patientId}/notes/{noteId}",
        {
          params: {
            path: { tenantId, patientId, noteId },
          },
          body: payload,
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to update patient note",
          "useUpdatePatientNote",
          response?.status,
        );
      }
    },
    onMutate: async ({ noteId, ...payload }) => {
      const listKey = patientNoteKeys.list(tenantId ?? "", patientId ?? "");
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<PatientNote[]>(listKey);

      queryClient.setQueryData<PatientNote[]>(listKey, (old) =>
        old?.map((n) =>
          n.id === noteId
            ? { ...n, ...payload, updatedAt: new Date().toISOString() }
            : n,
        ),
      );

      return { previous, listKey };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: patientNoteKeys.patient(tenantId ?? "", patientId ?? ""),
      });
    },
  });
}

/**
 * Hook to delete a patient note. Optimistically removes the row from the
 * list cache and rolls back on failure.
 */
export function useDeletePatientNote() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error, response } = await client.DELETE(
        "/{tenantId}/patients/{patientId}/notes/{noteId}",
        {
          params: {
            path: { tenantId, patientId, noteId },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to delete note",
          "useDeletePatientNote",
          response?.status,
        );
      }
      return { noteId };
    },
    onMutate: async (noteId) => {
      const listKey = patientNoteKeys.list(tenantId ?? "", patientId ?? "");
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<PatientNote[]>(listKey);
      queryClient.setQueryData<PatientNote[]>(listKey, (old) =>
        removePatientNote(old, noteId),
      );
      return { previous, listKey };
    },
    onError: (_error, _noteId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: patientNoteKeys.list(tenantId ?? "", patientId ?? ""),
      });
    },
  });
}
